import React, { useState, useCallback, useEffect } from 'react';
import { Check, Link2, Maximize2, RotateCcw } from 'lucide-react';
import { PCCase } from '../../types';
import { Button, Toggle, ToggleGroup, Card, Modal } from '../ui';
import { useAppStore } from '../../context/store';
import { ComparisonView2D } from './ComparisonView2D';
import { ComparisonTable } from './ComparisonTable';
import { Scene } from '../3d';
import { getSeriesColor } from '../../utils/seriesColors';

interface ComparisonPanelProps {
  cases: PCCase[];
}

export const ComparisonPanel: React.FC<ComparisonPanelProps> = ({ cases }) => {
  const {
    comparison,
    setViewMode,
    toggleOverlayMode,
    toggleGrid,
    clearComparison,
  } = useAppStore();

  const [isFullscreen, setIsFullscreen] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);

  useEffect(() => {
    if (!linkCopied) {
      return;
    }
    const timeout = window.setTimeout(() => setLinkCopied(false), 2000);
    return () => window.clearTimeout(timeout);
  }, [linkCopied]);

  const handleShare = useCallback(async () => {
    const url = new URL(window.location.href);
    url.searchParams.set('cases', cases.map((c) => c.id).join(','));
    try {
      await navigator.clipboard.writeText(url.toString());
      setLinkCopied(true);
    } catch {
      window.prompt('Copy this comparison link:', url.toString());
    }
  }, [cases]);

  if (cases.length === 0) {
    return null;
  }

  const visualization = comparison.viewMode === '2d' ? (
    <ComparisonView2D
      cases={cases}
      overlayMode={comparison.overlayMode}
      showGrid={comparison.showGrid}
    />
  ) : (
    <Scene
      cases={cases}
      showGrid={comparison.showGrid}
      overlayMode={comparison.overlayMode}
      showDimensions={true}
    />
  );

  return (
    <div className="space-y-6">
      {/* Controls */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex flex-wrap items-center gap-5">
          <ToggleGroup
            options={[
              { value: '2d', label: '2D front' },
              { value: '3d', label: '3D' },
            ]}
            value={comparison.viewMode}
            onChange={(v) => setViewMode(v as '2d' | '3d')}
          />
          <Toggle checked={comparison.showGrid} onChange={toggleGrid} label="Grid" size="sm" />
          <Toggle checked={comparison.overlayMode} onChange={toggleOverlayMode} label="Overlay" size="sm" />
        </div>

        <div className="flex items-center gap-1">
          <Button variant="ghost" size="icon" onClick={() => setIsFullscreen(true)} aria-label="Fullscreen view">
            <Maximize2 size={16} />
          </Button>
          <Button variant="ghost" onClick={handleShare} aria-live="polite">
            {linkCopied ? <Check size={16} /> : <Link2 size={16} />}
            {linkCopied ? 'Link copied' : 'Copy link'}
          </Button>
          <Button variant="ghost" onClick={clearComparison}>
            <RotateCcw size={16} />
            Clear
          </Button>
        </div>
      </div>

      {/* Visualization */}
      <Card className="overflow-hidden">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-line px-4 py-3">
          <span className="font-mono text-xs uppercase tracking-label text-muted">
            {comparison.viewMode === '2d' ? 'Front view · to scale' : '3D view · drag to rotate'}
          </span>
          <ul className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
            {cases.map((c, index) => (
              <li key={c.id} className="flex items-center gap-1.5">
                <span className="h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: getSeriesColor(index) }} aria-hidden="true" />
                {c.name}
              </li>
            ))}
          </ul>
        </div>
        <div className="relative h-[400px] overflow-hidden bg-surface-2 lg:h-[500px]">
          {visualization}
        </div>
      </Card>

      {/* Comparison Table */}
      <Card>
        <div className="border-b border-line px-4 py-3">
          <h2 className="font-mono text-xs uppercase tracking-label text-muted">Specifications</h2>
        </div>
        <ComparisonTable cases={cases} />
      </Card>

      {/* Fullscreen Modal */}
      <Modal
        isOpen={isFullscreen}
        onClose={() => setIsFullscreen(false)}
        size="full"
        title="Comparison view"
      >
        <div className="h-[70vh] overflow-hidden rounded-lg bg-surface-2">
          {visualization}
        </div>
      </Modal>
    </div>
  );
};
